<template>
  <div class="reserve-patient-appointments px-3 pb-3">
    <v-progress-linear
      v-if="loading"
      indeterminate
      height="2"
      color="primary"
      class="mb-2"
    />

    <v-alert v-if="errorMessage" dense text type="error" class="mb-2">
      {{ errorMessage }}
    </v-alert>

    <template v-if="loaded">
      <div v-if="!future.length && !past.length" class="grey--text text--darken-1 py-4 text-center">
        {{ $t('ReservePatientAppointments.noAppointments') }}
      </div>

      <section v-if="future.length" class="appointment-section">
        <div class="text-subtitle-2 font-weight-bold mb-1">
          {{ $t('ReservePatientAppointments.future') }}
        </div>
        <v-timeline dense class="appointment-timeline py-0">
          <v-timeline-item
            v-for="appointment in visibleFuture"
            :key="appointment.reserve_no"
            :color="statusMeta(appointment.attend_status).color"
            small
          >
            <v-card
              outlined
              class="appointment-card"
              role="button"
              tabindex="0"
              @click="openReserve(appointment)"
              @keyup.enter="openReserve(appointment)"
            >
              <v-card-text class="pa-2">
                <div class="d-flex align-center justify-space-between">
                  <span class="font-weight-medium black--text">{{ formatAppointmentTime(appointment) }}</span>
                  <v-chip x-small :color="statusMeta(appointment.attend_status).color" dark>
                    {{ statusMeta(appointment.attend_status).text }}
                  </v-chip>
                </div>
                <div class="text-caption grey--text text--darken-1 mt-1">
                  {{ appointmentProvider(appointment) }}
                </div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
        <v-btn
          v-if="future.length > defaultVisibleCount"
          text
          small
          color="primary"
          class="ml-6"
          @click="showAllFuture = !showAllFuture"
        >
          {{ futureToggleText }}
        </v-btn>
      </section>

      <div v-if="future.length && past.length" class="past-divider my-3">
        <span>{{ $t('ReservePatientAppointments.pastBefore', {date: formattedBaseDate}) }}</span>
      </div>

      <section v-if="past.length" class="appointment-section">
        <div v-if="!future.length" class="text-subtitle-2 font-weight-bold mb-1">
          {{ $t('ReservePatientAppointments.past') }}
        </div>
        <v-timeline dense class="appointment-timeline py-0">
          <v-timeline-item
            v-for="appointment in visiblePast"
            :key="appointment.reserve_no"
            :color="statusMeta(appointment.attend_status).color"
            small
          >
            <v-card
              outlined
              class="appointment-card"
              role="button"
              tabindex="0"
              @click="openReserve(appointment)"
              @keyup.enter="openReserve(appointment)"
            >
              <v-card-text class="pa-2">
                <div class="d-flex align-center justify-space-between">
                  <span class="font-weight-medium black--text">{{ formatAppointmentTime(appointment) }}</span>
                  <v-chip x-small :color="statusMeta(appointment.attend_status).color" dark>
                    {{ statusMeta(appointment.attend_status).text }}
                  </v-chip>
                </div>
                <div class="text-caption grey--text text--darken-1 mt-1">
                  {{ appointmentProvider(appointment) }}
                </div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
        <v-btn
          v-if="past.length > defaultVisibleCount"
          text
          small
          color="primary"
          class="ml-6"
          @click="showAllPast = !showAllPast"
        >
          {{ pastToggleText }}
        </v-btn>
      </section>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { getReservePatientAppointmentsAPI } from '@/api/reserve'

export default {
  name: 'ReservePatientAppointments',
  props: {
    patientCode: {
      type: String,
      required: true
    },
    baseDate: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      future: [],
      past: [],
      appointmentSummary: null,
      loading: false,
      loaded: false,
      errorMessage: '',
      showAllFuture: false,
      showAllPast: false,
      defaultVisibleCount: 5,
      requestGeneration: 0
    }
  },
  computed: {
    ...mapState({
      attendStatusMap: state => state.attendStatusMap
    }),
    ...mapGetters([
      'axiosParamData',
      'clinicUser'
    ]),
    visibleFuture() {
      return this.showAllFuture ? this.future : this.future.slice(0, this.defaultVisibleCount)
    },
    visiblePast() {
      return this.showAllPast ? this.past : this.past.slice(0, this.defaultVisibleCount)
    },
    futureToggleText() {
      if (this.showAllFuture) return this.$t('ReservePatientAppointments.collapseFuture')
      return this.$t('ReservePatientAppointments.expandFuture', {
        count: this.future.length - this.defaultVisibleCount
      })
    },
    pastToggleText() {
      if (this.showAllPast) return this.$t('ReservePatientAppointments.collapsePast')
      return this.$t('ReservePatientAppointments.expandPast', {
        count: this.past.length - this.defaultVisibleCount
      })
    },
    formattedBaseDate() {
      if (!this.baseDate) return ''
      return this.$moment(this.baseDate, 'YYYY-MM-DD').format('YYYY/M/D')
    }
  },
  mounted() {
    this.loadAppointments()
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    async loadAppointments() {
      const generation = ++this.requestGeneration
      this.loading = true
      this.errorMessage = ''

      try {
        const response = await getReservePatientAppointmentsAPI(this.patientCode, {
          ...this.axiosParamData,
          base_date: this.baseDate
        })

        if (generation !== this.requestGeneration) return

        const payload = response.data
        if (!payload || !Array.isArray(payload.future) || !Array.isArray(payload.past)) {
          throw new Error('Unexpected reserve patient appointments response')
        }

        this.future = payload.future
        this.past = payload.past
        this.appointmentSummary = payload.appointment_summary || null
        this.loaded = true
        this.$emit('summary-updated', {
          patientCode: this.patientCode,
          appointmentSummary: this.appointmentSummary
        })
      } catch (error) {
        if (generation !== this.requestGeneration) return
        this.errorMessage = this.$t('ReservePatientAppointments.loadFailed')
      } finally {
        if (generation === this.requestGeneration) this.loading = false
      }
    },
    refresh() {
      return this.loadAppointments()
    },
    statusMeta(status) {
      return this.attendStatusMap[String(status)] || {
        text: this.$t('ReservePatientAppointments.unknownStatus'),
        color: 'grey'
      }
    },
    formatAppointmentTime(appointment) {
      const start = this.$moment.parseZone(appointment.start_at)
      const end = this.$moment.parseZone(appointment.end_at)
      if (!start.isValid()) return ''
      if (!end.isValid()) return start.format('YYYY/M/D HH:mm')
      return `${start.format('YYYY/M/D HH:mm')}–${end.format('HH:mm')}`
    },
    appointmentProvider(appointment) {
      if (!this.clinicUser) return appointment.clinic_name || this.$t('ReservePatientAppointments.unknownClinic')

      const doctorName = appointment.doctor_name || this.$t('ReservePatientAppointments.unknownDoctor')
      return appointment.doctor_title ? `${doctorName}（${appointment.doctor_title}）` : doctorName
    },
    openReserve(appointment) {
      this.$emit('open-reserve', {
        reserveNo: appointment.reserve_no,
        patientCode: this.patientCode
      })
    }
  }
}
</script>

<style scoped>
.reserve-patient-appointments {
  background: #fafafa;
}

.appointment-timeline::v-deep .v-timeline-item__body {
  max-width: calc(100% - 36px);
}

.appointment-timeline::v-deep .v-timeline-item__divider {
  min-width: 36px;
}

.appointment-card {
  cursor: pointer;
}

.appointment-card:hover,
.appointment-card:focus {
  border-color: var(--v-primary-base);
  outline: none;
}

.past-divider {
  display: flex;
  align-items: center;
  color: #757575;
  font-size: 12px;
}

.past-divider::before,
.past-divider::after {
  content: '';
  flex: 1;
  border-top: 1px solid #e0e0e0;
}

.past-divider span {
  padding: 0 8px;
}
</style>
